import { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import axios from 'axios';
import foodTrackingService, { FoodItem } from '../../services/foodTrackingService';
import { isNative } from '../../services/platform.service';

const API_URL = import.meta.env.VITE_API_URL || 'https://fiifit.ro';

interface FoodScannerProps {
  onScanSuccess: (food: FoodItem) => void;
  onClose: () => void;
  mealType?: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  date?: string;
  onDirectAdd?: () => void;
}

export default function FoodScanner({ 
  onScanSuccess, 
  onClose,
  mealType,
  date,
  onDirectAdd 
}: FoodScannerProps) {
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [scannedFood, setScannedFood] = useState<FoodItem | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [cameraStarting, setCameraStarting] = useState(false);

  // Ref to Html5Qrcode instance — persists across renders (web only)
  const html5QrcodeRef = useRef<Html5Qrcode | null>(null);
  // Prevent double-processing of the same barcode
  const processingRef = useRef(false);
  // MLKit scanning active ref (for cleanup)
  const mlkitActiveRef = useRef(false);

  // ─── WEB: Start live camera stream ─────────────────────────────────────────
  useEffect(() => {
    // Only run on web — native uses MLKit
    if (!scanning || isNative()) return;

    let stopped = false;

    const startCamera = async () => {
      setCameraStarting(true);
      setError(null);

      // Small delay to ensure the div is rendered in DOM before html5-qrcode tries to use it
      await new Promise(resolve => setTimeout(resolve, 100));

      try {
        const qrcode = new Html5Qrcode('barcode-reader-region');
        html5QrcodeRef.current = qrcode;

        await qrcode.start(
          { facingMode: 'environment' }, // rear camera, no selector
          {
            fps: 10,
            qrbox: { width: 260, height: 160 }, // wide box — better for barcodes
            aspectRatio: 1.7778, // 16:9
          },
          async (decodedText) => {
            if (processingRef.current) return;
            processingRef.current = true;

            // Stop camera first, then process — prevents white screen
            try {
              await qrcode.stop();
              qrcode.clear();
            } catch (_) { /* ignore */ }
            html5QrcodeRef.current = null;

            if (!stopped) {
              // Use setTimeout to let React re-render before processing
              setTimeout(() => handleBarcodeFound(decodedText), 50);
            }
          },
          () => { /* scan failure — ignore, happens constantly */ }
        );

        setCameraStarting(false);
      } catch (err: any) {
        if (!stopped) {
          setCameraStarting(false);
          if (err?.message?.includes('ermission') || err?.name === 'NotAllowedError') {
            setError('Accesul la cameră a fost refuzat. Permite accesul la cameră din setările browserului și încearcă din nou.');
          } else if (err?.name === 'NotFoundError' || err?.message?.includes('not found')) {
            setError('Nu s-a găsit nicio cameră pe dispozitiv.');
          } else {
            setError('Nu s-a putut porni camera. Verifică permisiunile și încearcă din nou.');
          }
          setScanning(false);
        }
      }
    };

    startCamera();

    return () => {
      stopped = true;
      if (html5QrcodeRef.current) {
        html5QrcodeRef.current.stop().catch(() => {}).finally(() => {
          try { html5QrcodeRef.current?.clear(); } catch (_) { /* ignore */ }
          html5QrcodeRef.current = null;
          processingRef.current = false;
        });
      }
    };
  }, [scanning]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Cleanup MLKit on unmount ───────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (mlkitActiveRef.current) {
        stopMLKitScanner();
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── MLKit: stop scanner ────────────────────────────────────────────────────
  const stopMLKitScanner = async () => {
    try {
      const { BarcodeScanner } = await import('@capacitor-mlkit/barcode-scanning');
      await BarcodeScanner.stopScan();
      document.querySelector('body')?.classList.remove('barcode-scanner-active');
      mlkitActiveRef.current = false;
    } catch (_) { /* ignore */ }
  };

  // ─── NATIVE (iOS/Android): MLKit real-time scanner ─────────────────────────
  const handleNativeScan = async () => {
    setError(null);
    processingRef.current = false;
    setLoading(false);

    try {
      const { BarcodeScanner, BarcodeFormat, GoogleBarcodeScannerModuleInstallState } =
        await import('@capacitor-mlkit/barcode-scanning');

      // Check if Google Barcode Scanner module is available (Android only)
      const { available } = await BarcodeScanner.isGoogleBarcodeScannerModuleAvailable().catch(() => ({ available: true }));

      if (!available) {
        // Install the module first (Android)
        await BarcodeScanner.installGoogleBarcodeScannerModule();
        // Wait for installation
        await new Promise<void>((resolve, reject) => {
          BarcodeScanner.addListener('googleBarcodeScannerModuleInstallProgress', (event: any) => {
            if (event.state === GoogleBarcodeScannerModuleInstallState.COMPLETED) {
              resolve();
            } else if (event.state === GoogleBarcodeScannerModuleInstallState.FAILED) {
              reject(new Error('Instalare eșuată'));
            }
          });
          // Timeout after 30s
          setTimeout(() => resolve(), 30000);
        });
      }

      // Request camera permission
      const { camera } = await BarcodeScanner.requestPermissions();
      if (camera === 'denied') {
        setError('Accesul la cameră a fost refuzat. Permite accesul la cameră din Setări → FII FIT → Cameră.');
        return;
      }

      // Make background transparent so camera shows through WebView
      document.querySelector('body')?.classList.add('barcode-scanner-active');
      mlkitActiveRef.current = true;
      setScanning(true);

      // Start scanning
      const { barcodes } = await BarcodeScanner.scan({
        formats: [
          BarcodeFormat.Ean13,
          BarcodeFormat.Ean8,
          BarcodeFormat.UpcA,
          BarcodeFormat.UpcE,
          BarcodeFormat.Code128,
          BarcodeFormat.Code39,
          BarcodeFormat.QrCode,
          BarcodeFormat.DataMatrix,
        ],
      });

      // Scanner closed / result received
      document.querySelector('body')?.classList.remove('barcode-scanner-active');
      mlkitActiveRef.current = false;
      setScanning(false);

      if (barcodes && barcodes.length > 0) {
        const barcode = barcodes[0].rawValue;
        if (barcode) {
          await handleBarcodeFound(barcode);
        }
      }
    } catch (err: any) {
      document.querySelector('body')?.classList.remove('barcode-scanner-active');
      mlkitActiveRef.current = false;
      setScanning(false);

      // User cancelled — don't show error
      if (
        err?.message?.includes('cancelled') ||
        err?.message?.includes('cancel') ||
        err?.message?.includes('User cancelled') ||
        err?.message?.includes('scan was cancelled')
      ) {
        return;
      }

      // MLKit not available — fallback to camera photo
      if (
        err?.message?.includes('not implemented') ||
        err?.message?.includes('not available') ||
        err?.message?.includes('MLKit')
      ) {
        await handleNativeFallback();
        return;
      }

      if (err?.message?.includes('ermission') || err?.message?.includes('denied')) {
        setError('Accesul la cameră a fost refuzat. Permite accesul la cameră din Setări → FII FIT → Cameră.');
      } else {
        setError('Nu s-a putut porni scanner-ul. Încearcă din nou.');
      }
    }
  };

  // ─── NATIVE Fallback: Camera photo → html5-qrcode scanFile ─────────────────
  const handleNativeFallback = async () => {
    setError(null);
    processingRef.current = false;
    setLoading(true);

    try {
      const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');

      const photo = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera,
        direction: 'REAR' as any,
        promptLabelHeader: 'Scanează codul de bare',
        promptLabelPhoto: 'Alege din galerie',
        promptLabelPicture: 'Fotografiază codul de bare',
      });

      if (!photo.dataUrl) {
        setLoading(false);
        return;
      }

      const response = await fetch(photo.dataUrl);
      const blob = await response.blob();
      const file = new File([blob], 'barcode.jpg', { type: 'image/jpeg' });

      const qrcode = new Html5Qrcode('barcode-reader-region-native');
      try {
        const result = await qrcode.scanFile(file, false);
        await handleBarcodeFound(result);
      } catch (_) {
        setError('Codul de bare nu a putut fi citit din imagine. Încearcă să fotografiezi mai aproape și mai clar.');
        setLoading(false);
      } finally {
        qrcode.clear();
      }
    } catch (err: any) {
      setLoading(false);
      if (err?.message?.includes('cancelled') || err?.message?.includes('cancel') || err?.message?.includes('User cancelled')) {
        return;
      }
      if (err?.message?.includes('ermission') || err?.message?.includes('denied')) {
        setError('Accesul la cameră a fost refuzat. Permite accesul la cameră din Setări → FII FIT → Cameră.');
      } else {
        setError('Nu s-a putut accesa camera. Încearcă din nou.');
      }
    }
  };

  // ─── Common: process found barcode ─────────────────────────────────────────
  const handleBarcodeFound = async (decodedText: string) => {
    setLoading(true);
    setScanning(false);
    setError(null);

    try {
      const food = await foodTrackingService.searchByBarcode(decodedText);

      if (food) {
        if (mealType && date && onDirectAdd) {
          setScannedFood(food);
          setShowConfirm(true);
        } else {
          onScanSuccess(food);
        }
      } else {
        setError('Produsul nu a fost găsit în baza de date. Încearcă să-l adaugi manual.');
      }
    } catch (err: any) {
      setError(err.message || 'Eroare la căutarea produsului');
    } finally {
      setLoading(false);
      processingRef.current = false;
    }
  };

  const handleDirectAdd = async () => {
    if (!scannedFood || !mealType || !date) return;
    
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('accessToken');

      // Use serving size as quantity (grams), nutrition values are per 100g
      const grams = scannedFood.servingSize || 100;
      const multiplier = grams / 100;

      // Build a proper ISO datetime with timezone offset so the server stores the correct UTC time
      const now = new Date();
      const [year, month, day] = date.split('-').map(Number);
      const localDate = new Date(year, month - 1, day, now.getHours(), now.getMinutes(), now.getSeconds());
      const consumedAt = localDate.toISOString();

      await axios.post(
        `${API_URL}/api/food-tracking/entries`,
        {
          foodName: scannedFood.name,
          brand: scannedFood.brand || undefined,
          barcode: scannedFood.barcode || undefined,
          servingSize: grams,
          servingUnit: scannedFood.servingUnit || 'g',
          quantity: grams,
          nutrition: {
            calories: Math.round((scannedFood.nutrition.calories ?? 0) * multiplier),
            protein: Math.round((scannedFood.nutrition.protein ?? 0) * multiplier * 10) / 10,
            carbs: Math.round((scannedFood.nutrition.carbs ?? 0) * multiplier * 10) / 10,
            fat: Math.round((scannedFood.nutrition.fat ?? 0) * multiplier * 10) / 10,
            fiber: scannedFood.nutrition.fiber != null ? Math.round(scannedFood.nutrition.fiber * multiplier * 10) / 10 : undefined,
            sugar: scannedFood.nutrition.sugar != null ? Math.round(scannedFood.nutrition.sugar * multiplier * 10) / 10 : undefined,
            sodium: scannedFood.nutrition.sodium != null ? Math.round(scannedFood.nutrition.sodium * multiplier * 10) / 10 : undefined,
          },
          mealType,
          date: consumedAt,
          imageUrl: scannedFood.imageUrl || undefined,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (onDirectAdd) onDirectAdd();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Eroare la adăugarea produsului');
      setShowConfirm(true); // keep confirm modal open to show error
    } finally {
      setLoading(false);
    }
  };

  const handleEditBeforeAdd = () => {
    if (scannedFood) onScanSuccess(scannedFood);
  };

  const startWebScanning = () => {
    setError(null);
    processingRef.current = false;
    setScanning(true);
  };

  const stopScanning = async () => {
    if (isNative() && mlkitActiveRef.current) {
      await stopMLKitScanner();
    }
    setScanning(false);
    onClose();
  };

  const retryScanning = () => {
    setError(null);
    processingRef.current = false;
    if (isNative()) {
      handleNativeScan();
    } else {
      setScanning(true);
    }
  };

  // ─── Confirmation modal ─────────────────────────────────────────────────────
  if (showConfirm && scannedFood) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <span className="text-2xl">✅</span>
              Produs Identificat
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-6">
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 mb-6 border border-green-200">
              <h3 className="text-xl font-bold text-gray-900 mb-2">{scannedFood.name}</h3>
              {scannedFood.brand && <p className="text-sm text-gray-600 mb-3">{scannedFood.brand}</p>}
              {scannedFood.barcode && <p className="text-xs text-gray-500 mb-3">Barcode: {scannedFood.barcode}</p>}
              
              <div className="grid grid-cols-4 gap-3 mb-4">
                <div className="text-center bg-white rounded-lg p-3">
                  <div className="text-2xl font-bold text-primary-600">{scannedFood.nutrition.calories}</div>
                  <div className="text-xs text-gray-600 mt-1">kcal</div>
                </div>
                <div className="text-center bg-white rounded-lg p-3">
                  <div className="text-xl font-bold text-blue-600">{scannedFood.nutrition.protein}g</div>
                  <div className="text-xs text-gray-600 mt-1">Proteine</div>
                </div>
                <div className="text-center bg-white rounded-lg p-3">
                  <div className="text-xl font-bold text-yellow-600">{scannedFood.nutrition.carbs}g</div>
                  <div className="text-xs text-gray-600 mt-1">Carbo</div>
                </div>
                <div className="text-center bg-white rounded-lg p-3">
                  <div className="text-xl font-bold text-orange-600">{scannedFood.nutrition.fat}g</div>
                  <div className="text-xs text-gray-600 mt-1">Grăsimi</div>
                </div>
              </div>

              <div className="text-sm text-gray-700">
                <span className="font-medium">Porție:</span> {scannedFood.servingSize}{scannedFood.servingUnit}
              </div>
            </div>

            {/* Error display in confirm modal */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <button
                onClick={handleDirectAdd}
                disabled={loading}
                className="w-full py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:shadow-lg transition-all font-bold text-lg disabled:opacity-50"
              >
                {loading ? 'Adaug...' : '✓ Adaugă Direct la Masă'}
              </button>
              
              <button
                onClick={handleEditBeforeAdd}
                className="w-full py-3 border-2 border-primary-300 text-primary-600 rounded-xl hover:bg-primary-50 transition-all font-medium"
              >
                Editează Cantitatea
              </button>

              <button
                onClick={onClose}
                className="w-full py-3 border border-gray-300 text-gray-600 rounded-xl hover:bg-gray-50 transition-all"
              >
                Anulează
              </button>
            </div>

            <p className="text-xs text-gray-400 text-center mt-4 pt-4 border-t border-gray-100">
              Date nutriționale furnizate de{' '}
              <a href="https://world.openfoodfacts.org" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">
                Open Food Facts
              </a>
              {' '}— licență{' '}
              <a href="https://opendatacommons.org/licenses/odbl/1.0/" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">
                ODbL
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ─── Main scanner UI ────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-lg w-full p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Scanează Barcode</h2>
          <button onClick={stopScanning} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ── NATIVE (iOS/Android): MLKit real-time scanner ── */}
        {isNative() && !loading && !error && !scanning && (
          <div className="text-center py-8">
            <div className="mb-6 flex justify-center">
              <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
                  />
                </svg>
              </div>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Scanează codul de bare
            </h3>
            <p className="text-gray-600 mb-6">
              Scanner-ul nativ pornește automat — îndreaptă camera spre codul de bare de pe ambalaj
            </p>
            <button
              onClick={handleNativeScan}
              className="bg-green-600 text-white px-8 py-4 rounded-xl hover:bg-green-700 transition-colors font-bold text-lg shadow-lg"
            >
              📷 Pornește Scanner
            </button>
            {/* Hidden div needed for html5-qrcode scanFile fallback on native */}
            <div id="barcode-reader-region-native" className="hidden" />
          </div>
        )}

        {/* ── NATIVE: scanning active overlay message ── */}
        {isNative() && scanning && !loading && (
          <div className="text-center py-8">
            <div className="flex justify-center mb-4">
              <div className="animate-pulse w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
            </div>
            <p className="text-gray-700 font-medium mb-2">Scanner activ...</p>
            <p className="text-gray-500 text-sm mb-6">Îndreaptă camera spre codul de bare</p>
            <button
              onClick={stopScanning}
              className="text-sm text-red-500 underline hover:text-red-700"
            >
              Anulează
            </button>
          </div>
        )}

        {/* ── WEB: live camera viewfinder ── */}
        {!isNative() && (
          <>
            {/* Idle — before camera starts */}
            {!scanning && !loading && !error && (
              <div className="text-center py-8">
                <div className="mb-6 flex justify-center">
                  <svg className="w-24 h-24 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Scanează codul de bare al produsului
                </h3>
                <p className="text-gray-600 mb-6">
                  Camera din spate pornește automat — poziționează codul de bare în cadru
                </p>
                <button
                  onClick={startWebScanning}
                  className="bg-primary-600 text-white px-8 py-3 rounded-lg hover:bg-primary-700 transition-colors font-medium"
                >
                  📷 Pornește Camera
                </button>
              </div>
            )}

            {/* Camera starting spinner */}
            {cameraStarting && (
              <div className="text-center py-8">
                <div className="flex justify-center mb-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
                </div>
                <p className="text-gray-600">Se pornește camera...</p>
              </div>
            )}

            {/* Live viewfinder — div must exist in DOM when scanning=true */}
            <div className={scanning ? 'block' : 'hidden'}>
              <div
                id="barcode-reader-region"
                className="rounded-xl overflow-hidden w-full"
                style={{ minHeight: '240px' }}
              />
              {!cameraStarting && (
                <div className="mt-4 text-center">
                  <p className="text-sm text-gray-600 mb-3">
                    📦 Îndreaptă camera spre codul de bare de pe ambalaj
                  </p>
                  <button
                    onClick={stopScanning}
                    className="text-sm text-gray-500 underline hover:text-gray-700"
                  >
                    Anulează
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {/* Loading — searching product */}
        {loading && (
          <div className="text-center py-8">
            <div className="flex justify-center mb-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
            </div>
            <p className="text-gray-600">Căutăm produsul...</p>
          </div>
        )}

        {/* Error state */}
        {error && !loading && (
          <div className="py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={retryScanning}
                className="flex-1 bg-primary-600 text-white py-3 rounded-lg hover:bg-primary-700 transition-colors font-medium"
              >
                🔄 Încearcă din nou
              </button>
              <button
                onClick={onClose}
                className="flex-1 border border-gray-300 text-gray-600 py-3 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Închide
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        {!scanning && !loading && (
          <div className="mt-6 pt-6 border-t border-gray-100 space-y-2">
            <p className="text-xs text-gray-500 text-center">
              💡 Sfat: Asigură-te că codul de bare este clar vizibil și bine iluminat
            </p>
            <p className="text-xs text-gray-400 text-center">
              Date nutriționale furnizate de{' '}
              <a href="https://world.openfoodfacts.org" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">
                Open Food Facts
              </a>
              {' '}— licență{' '}
              <a href="https://opendatacommons.org/licenses/odbl/1.0/" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">
                ODbL
              </a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
